import { VariableIcon } from '@heroicons/react/24/outline'
import { addHours } from 'date-fns'
import {
  camelCase,
  drop,
  find,
  get,
  isEmpty,
  isArray as isLodashArray,
  join,
  kebabCase,
  pick,
  set,
  sortBy,
  startCase
} from 'lodash'

import { ActionFeature } from '@/components/Icons'
import { ImagePreview } from '@/components/ImagePreview'
import { StyledArrowsOpposite, StyledScheduledIcon } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import convertJSONtoSchema from '@/pages/SpaceAppBuilder/utilities/convertJSONToSchema'
import {
  Config,
  DataSourceEventTrigger,
  DataSourceMapping,
  Feature,
  FeatureTrigger,
  FieldTypes,
  FlowElement,
  HttpTrigger,
  KnowledgeTrigger,
  LabeledDataObject,
  ManualTrigger,
  MicroappViewTrigger,
  NotificationActionTrigger,
  ScheduledTrigger,
  SourcePropertyObject,
  Step,
  StepType,
  TriggerInput,
  TriggerTypes,
  Variable,
  VariableProperties,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'

import FeatureTriggerIcon from './images/operations/FeatureTriggerIcon'
import KnowledgeIcon from './images/operations/KnowledgeIcon'
import UIView from './images/operations/UIView'

interface VariableBuilderStepData {
  stepName: string
  stepLabel: string
  icon: string | JSX.Element
  iconUrl?: string
  stepType: StepType | TriggerTypes
}

interface BuildDataSourceStepVariablesProps {
  step: Step
  dataSourcesMapping: Record<string, any>
  stepElement?: LabeledDataObject | FlowElement
  isWorkgridDataSource?: boolean
}

const getStepModelData = (step: Step, dataSourcesMapping: Record<string, any>) => {
  const actions = dataSourcesMapping[step.stepData?.dataSourceKey]?.actions
  if (actions) {
    const action = step.stepData?.action
      ? actions[step.stepData?.action]
      : find(
          actions,
          value => value.legacy?.path === step.stepData.path && value.legacy?.method === step.stepData.method
        )
    return action?.response
  } else {
    // get old model data
    const endpoint = step.stepData.path + ':' + step.stepData.method

    let stepDataSourceMapping
    if (!isEmpty(dataSourcesMapping)) {
      stepDataSourceMapping = dataSourcesMapping[step.stepData.dataSourceKey]?.find(
        (ds: DataSourceMapping) =>
          ds.path + ':' + ds.method === endpoint || kebabCase(ds.path) + ':' + ds.method === endpoint
      )
    }
    return stepDataSourceMapping?.response?.model
  }
}

const wrapWithJsonPathVariable = (value: string) => `$.${value}`

export const buildDataSourceTriggerVariables = (
  dataSourcesMapping: Record<string, any>,
  trigger: DataSourceEventTrigger,
  triggerEventIsCustom?: boolean
) => {
  const variableData: Variable[] = []
  let triggerVariables: Variable[] = []

  const triggerEventNameForPath = trigger.triggerData.triggerEvent

  if (trigger.triggerData && !dataSourcesMapping[trigger.triggerData.triggerSourceKey]?.events) {
    const triggerDataSourceMapping = dataSourcesMapping[trigger.triggerData.triggerSourceKey]?.find(
      (dsm: DataSourceMapping) => {
        return (
          (dsm.path === `/${triggerEventIsCustom ? triggerEventNameForPath : kebabCase(triggerEventNameForPath)}` ||
            dsm.path === `/${triggerEventNameForPath}`) &&
          dsm.method === 'get'
        )
      }
    )

    if (triggerDataSourceMapping) {
      const modelData = triggerDataSourceMapping.response.model

      variableData.push({
        name: wrapWithJsonPathVariable(`trigger.event.eventType`),
        stepName: trigger.name,
        stepLabel: 'Trigger',
        displayName: 'Event Type',
        enum: ['CREATED', 'UPDATED'],
        type: FieldTypes.STRING,
        icon: trigger.triggerData.triggerSourceImageUrl
      })
      trigger.triggerData.watchedFields?.forEach((field: string) => {
        variableData.push({
          name: wrapWithJsonPathVariable(`trigger.event.watchedFields['${field}Changed']`),
          stepName: trigger.name,
          stepLabel: 'Trigger',
          enum: ['true', 'false'],
          displayName: startCase(`${field}Changed`),
          type: FieldTypes.BOOLEAN,
          icon: trigger.triggerData.triggerSourceImageUrl
        })
      })

      const objectName = modelData['x-vendor-objectname']
      variableData.push({
        name: wrapWithJsonPathVariable(`trigger.event`),
        stepName: trigger.name,
        stepLabel: 'Trigger',
        displayName: startCase(objectName || trigger.triggerData.triggerEvent),
        type: FieldTypes.OBJECT,
        icon: trigger.triggerData.triggerSourceImageUrl
      })

      const variableTriggerStepData = {
        stepName: trigger.name || 'Trigger',
        stepLabel: 'Trigger',
        stepType: trigger.type,
        icon: trigger.triggerData.triggerSourceImageUrl
      }

      if (modelData?.properties) {
        triggerVariables = getVariablesFromJsonSchema(
          variableTriggerStepData,
          modelData.properties,
          wrapWithJsonPathVariable('trigger.event'),
          false // this should always be an object
        )
        triggerVariables = sortBy(triggerVariables, ['name'])
      }
    }
  }

  return variableData.concat(triggerVariables)
}

export const buildWorkgridDataSourceTriggerVariables = (
  dataSourcesMapping: Record<string, any>,
  trigger: DataSourceEventTrigger
) => {
  const variableData: Variable[] = []
  let triggerVariables: Variable[] = []

  if (dataSourcesMapping?.[trigger.triggerData.triggerSourceKey]?.events?.[trigger.triggerData.triggerEvent]) {
    const modelData =
      dataSourcesMapping[trigger.triggerData.triggerSourceKey]?.events[trigger.triggerData.triggerEvent].eventModel

    variableData.push({
      name: wrapWithJsonPathVariable(`trigger.event.eventType`),
      stepName: trigger.name,
      stepLabel: 'Trigger',
      displayName: 'Event Type',
      enum: ['CREATED', 'UPDATED'],
      type: FieldTypes.STRING,
      icon: trigger.triggerData.triggerSourceImageUrl
    })
    trigger.triggerData.watchedFields?.forEach((field: string) => {
      variableData.push({
        name: wrapWithJsonPathVariable(`trigger.event.watchedFields['${field}Changed']`),
        stepName: trigger.name,
        stepLabel: 'Trigger',
        enum: ['true', 'false'],
        displayName: startCase(`${field}Changed`),
        type: FieldTypes.BOOLEAN,
        icon: trigger.triggerData.triggerSourceImageUrl
      })
    })

    variableData.push({
      name: wrapWithJsonPathVariable(`trigger.event`),
      stepName: trigger.name,
      stepLabel: 'Trigger',
      displayName: startCase(trigger.triggerData.triggerEvent),
      type: FieldTypes.OBJECT,
      icon: trigger.triggerData.triggerSourceImageUrl
    })

    const variableTriggerStepData = {
      stepName: trigger.name || 'Trigger',
      stepLabel: 'Trigger',
      stepType: trigger.type,
      icon: trigger.triggerData.triggerSourceImageUrl
    }

    if (modelData?.properties) {
      triggerVariables = getVariablesFromJsonSchema(
        variableTriggerStepData,
        modelData.properties,
        wrapWithJsonPathVariable('trigger.event'),
        false // this should always be an object
      )
      triggerVariables = sortBy(triggerVariables, ['name'])
    }
  }

  return variableData.concat(triggerVariables)
}

export const buildMicroappEventVariables = (trigger: MicroappViewTrigger, user: any) => {
  const variableData: Variable[] = []
  const eventPropertiesVariableData: Variable[] = [
    ...buildUserVariables(trigger, user, <UIView />),
    ...buildVariablesFromTriggerPayload(trigger, <UIView />),
    {
      name: wrapWithJsonPathVariable(`trigger.event.spaceId`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'Space ID',
      type: FieldTypes.STRING,
      icon: <UIView />
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.tenantId`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'Tenant ID',
      type: FieldTypes.STRING,
      icon: <UIView />
    }
  ]
  variableData.push({
    name: wrapWithJsonPathVariable(`trigger.event`),
    stepName: trigger.name,
    stepLabel: 'Trigger',
    displayName: 'Event',
    type: FieldTypes.OBJECT,
    icon: <UIView />,
    properties: eventPropertiesVariableData
  })
  return variableData
}

export const buildManualEventVariables = (trigger: ManualTrigger, user: any) => {
  const variableData: Variable[] = []
  const eventPropertiesVariableData: Variable[] = []
  eventPropertiesVariableData.push(...buildUserVariables(trigger, user, <FeatureTriggerIcon />))
  variableData.push({
    name: wrapWithJsonPathVariable(`trigger.event`),
    stepName: trigger.name,
    stepLabel: 'Trigger',
    displayName: 'Event',
    type: FieldTypes.OBJECT,
    icon: <FeatureTriggerIcon />,
    properties: eventPropertiesVariableData
  })
  return variableData
}

export const buildKnowledgeEventVariables = (trigger: KnowledgeTrigger, user: any) => {
  const eventPropertiesVariableData: Variable[] = [
    ...buildUserVariables(trigger, user, <KnowledgeIcon />),
    {
      name: wrapWithJsonPathVariable(`trigger.event.utterance`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'Utterance',
      type: FieldTypes.STRING,
      icon: <KnowledgeIcon />
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.contractedUtterance`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'Contracted Utterance',
      type: FieldTypes.STRING,
      icon: <KnowledgeIcon />
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.expandedUtterance`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'Expanded Utterance',
      type: FieldTypes.STRING,
      icon: <KnowledgeIcon />
    }
  ]

  const variableData: Variable[] = [
    {
      name: wrapWithJsonPathVariable(`trigger.event`),
      stepName: trigger.name,
      stepLabel: 'Trigger',
      displayName: 'Event',
      type: FieldTypes.OBJECT,
      icon: <KnowledgeIcon />,
      properties: eventPropertiesVariableData
    }
  ]

  return variableData
}

export const buildFeatureVariables = (features: Feature[] | undefined, agentsFlagOn = false) => {
  const variableData: Variable[] = []
  features?.map(feature =>
    variableData.push({
      name: wrapWithJsonPathVariable(`feature.${feature.id}`),
      stepName: camelCase(feature.name),
      stepLabel: `${agentsFlagOn ? 'Capability' : 'Feature'} Trigger`,
      displayName: feature.name,
      type: FieldTypes.STRING,
      isHidden: false,
      icon: <FeatureTriggerIcon />,
      example: feature.name
    })
  )
  return variableData
}

export const buildConfigVariables = (config: Config[]) => {
  let variableData: Variable[] = []
  config?.forEach(config => {
    variableData.push({
      name: wrapWithJsonPathVariable(`config.${config.name}`),
      stepName: camelCase(config.name),
      stepLabel: 'Config',
      displayName: config.name,
      type: config.type,
      isHidden: false,
      icon: <VariableIcon className="h-[20px] w-[20px] rounded text-blue-700" />,
      example: config.defaultValue
    })
    if (config.type === 'object') {
      let fileUploadModel
      if (config.formInputType === 'file-upload') {
        const defaultUploadModel = {
          name: 'file-name.pdf',
          size: '1024',
          assetId: 'ee8d-45ff-9d6b-f984e789c4e3',
          url: 'https://workgridsoftware.workgrid.com/document'
        }
        fileUploadModel = convertJSONtoSchema(isEmpty(config.defaultValue) ? defaultUploadModel : config.defaultValue)
      } else {
        fileUploadModel = convertJSONtoSchema(config.defaultValue)
      }

      const variableStepData = {
        stepName: camelCase(config.name),
        stepLabel: 'Config',
        stepType: StepType.SetConfigVariable,
        icon: <VariableIcon className="h-[20px] w-[20px] rounded text-blue-700" />
      }
      if (fileUploadModel.properties) {
        const configVariables = getVariablesFromJsonSchema(
          variableStepData,
          // @ts-expect-error
          fileUploadModel.properties,
          wrapWithJsonPathVariable(`config.${config.name}`),
          false
        )
        variableData = variableData.concat(configVariables)
      }
    }
  })

  return variableData
}

export const buildDataSourceConfigVariables = (
  dataSourceInstances: { data: WorkgridDatasource }[] = [],
  dataSourcesMapping: Record<string, any>
) => {
  const variableData: Variable[] = []
  for (const dataSource of dataSourceInstances) {
    const config = pick(dataSource.data?.configuration, ['baseUrl', 'domain', 'subDomain', 'company'])
    Object.keys(config).map(configKey =>
      variableData.push({
        name: wrapWithJsonPathVariable(`dataSource.${dataSource.data.datasourceId}.${configKey}`),
        stepName: dataSourcesMapping[dataSource.data.datasourceId]?.name,
        stepLabel: 'Data Source',
        displayName: configKey,
        type: FieldTypes.STRING,
        isHidden: false,
        icon: <VariableIcon className="h-[20px] w-[20px] rounded text-blue-700" />,
        example: configKey
      })
    )
  }
  return variableData
}

export const buildApplicationVariables = (app: any, agentsFlagOn: boolean) => {
  const variableData: Variable[] = [
    {
      name: wrapWithJsonPathVariable(`app.name`),
      stepName: camelCase(app?.name),
      stepLabel: `${agentsFlagOn ? 'Agent' : 'App'}`,
      displayName: 'Name',
      type: FieldTypes.STRING,
      isHidden: false,
      icon: (
        <ImagePreview
          className="h-[20px] w-[20px] rounded"
          src={app?.iconUrl}
          // @ts-expect-error
          fallbackImage={FeatureTriggerIcon}
          useLoadingImage={false}
        />
      ),
      example: app?.name
    },
    {
      name: wrapWithJsonPathVariable(`app.description`),
      stepName: camelCase(app?.name),
      stepLabel: 'App',
      displayName: 'Description',
      type: FieldTypes.STRING,
      isHidden: false,
      icon: (
        <ImagePreview
          className="h-[20px] w-[20px] rounded"
          src={app?.iconUrl}
          // @ts-expect-error
          fallbackImage={FeatureTriggerIcon}
          useLoadingImage={false}
        />
      ),
      example: app?.description
    },
    {
      name: wrapWithJsonPathVariable(`app.iconUrl`),
      stepName: camelCase(app?.name),
      stepLabel: 'App',
      displayName: 'Icon Url',
      type: FieldTypes.STRING,
      isHidden: false,
      icon: (
        <ImagePreview
          className="h-[20px] w-[20px] rounded"
          src={app?.iconUrl}
          // @ts-expect-error
          fallbackImage={FeatureTriggerIcon}
          useLoadingImage={false}
        />
      ),
      example: app?.iconUrl
    }
  ]
  return variableData
}

export const buildNotificationActionTriggerVariables = (trigger: NotificationActionTrigger) => {
  const variableData: Variable[] = [
    {
      name: wrapWithJsonPathVariable(`trigger.event`),
      stepName: trigger.name,
      stepLabel: 'Trigger',
      displayName: 'Event',
      type: FieldTypes.OBJECT,
      icon: <ActionFeature />
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.userEmail`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'User Email',
      type: FieldTypes.STRING,
      icon: <ActionFeature />
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.userId`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'User ID',
      type: FieldTypes.STRING,
      icon: <ActionFeature />
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.actionTitle`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      enum: trigger.triggerData.actionSetButtonsLabels,
      displayName: 'Action Label',
      type: FieldTypes.STRING,
      icon: <ActionFeature />
    }
  ]
  if (trigger.triggerData.customEventData) {
    trigger.triggerData.customEventData?.forEach((triggerData: any) => {
      variableData.push({
        name: wrapWithJsonPathVariable(`trigger.event.${triggerData.attributeName}`),
        stepName: 'Event',
        stepLabel: 'Trigger',
        displayName: triggerData.attributeName,
        type: FieldTypes.STRING,
        icon: <ActionFeature />
      })
    })
  }
  return variableData
}

export const buildScheduledEventTriggerVariables = (trigger: ScheduledTrigger, user: any) => {
  const userVariableData: Variable[] = []

  if (trigger.triggerData.isUserSchedule) {
    const userObject = pick(user, ['email', 'displayName', 'title', 'preferredLanguage', 'timezone', 'externalId'])
    const name = get(user, 'name')
    const enterpriseUserAttributes = get(user, 'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User')
    const customAttributes = get(user, `urn:ietf:params:scim:schemas:extension:workgrid:2.0:${user?.organizationId}`)
    const userVariables = { ...userObject, ...name, ...customAttributes, ...enterpriseUserAttributes }

    Object.keys(userVariables).forEach(key => {
      userVariableData.push({
        name: wrapWithJsonPathVariable(`trigger.event.user.${key}`),
        stepName: 'User',
        stepLabel: 'Trigger',
        displayName: startCase(key),
        type: FieldTypes.STRING,
        icon: <StyledScheduledIcon />,
        example: userVariables[key]
      })
    })
  }
  const variableData: Variable[] = [
    {
      name: wrapWithJsonPathVariable(`trigger.event`),
      stepName: trigger.name,
      stepLabel: 'Trigger',
      displayName: 'Event',
      type: FieldTypes.OBJECT,
      icon: <StyledScheduledIcon />
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.currentExecutionTime`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'Current Execution Time',
      type: FieldTypes.STRING,
      icon: <StyledScheduledIcon />,
      example: addHours(new Date(), 3).toISOString()
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.lastExecutionTime`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'Last Execution Time',
      type: FieldTypes.STRING,
      icon: <StyledScheduledIcon />,
      example: addHours(new Date(), 1).toISOString()
    }
  ]
  if (trigger.triggerData.isUserSchedule) {
    variableData.push({
      name: wrapWithJsonPathVariable(`trigger.event.user`),
      stepName: 'User',
      stepLabel: 'Trigger',
      displayName: 'User',
      properties: userVariableData,
      type: FieldTypes.OBJECT,
      icon: <StyledScheduledIcon />
    })
  }

  return variableData
}

export const buildDataSourceStepVariables = ({
  step,
  stepElement,
  dataSourcesMapping,
  isWorkgridDataSource = false
}: BuildDataSourceStepVariablesProps) => {
  let stepVariables: Variable[] = []
  let objectName: string | undefined
  const modelData = getStepModelData(step, dataSourcesMapping)
  if (modelData) {
    const isArray = modelData.type === FieldTypes.ARRAY
    objectName = modelData['x-vendor-objectname']

    const variableStepData = {
      stepName: step.name,
      stepType: isWorkgridDataSource ? StepType.WorkgridDataSource : StepType.DataSource,
      stepLabel: stepElement?.data.label,
      icon: stepElement?.data.icon,
      iconUrl: stepElement?.data.iconUrl
    }
    if (modelData?.properties) {
      const filteredProperties = {}
      if (!isEmpty(stepElement?.data?.config?.stepData?.input?.query?.fields)) {
        for (const key in modelData.properties) {
          for (const fieldFilter of stepElement?.data.config.stepData.input.query.fields ?? []) {
            if (key === fieldFilter) {
              set(filteredProperties, key, modelData.properties[key])
              break
            }
          }
        }
      }

      stepVariables = getVariablesFromJsonSchema(
        variableStepData,
        isEmpty(filteredProperties) ? modelData.properties : filteredProperties,
        wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
        isArray
      )

      stepVariables = sortBy(stepVariables, ['name'])
    }
  }
  const initialStep = {
    name: wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
    stepName: step.name,
    stepLabel: stepElement?.data.label,
    displayName: startCase(objectName || step.stepData.path),
    type: modelData?.type || FieldTypes.OBJECT,
    icon: stepElement?.data.iconUrl
  }
  stepVariables.unshift(initialStep)
  return stepVariables
}

export const buildDefaultStepVariables = (
  step: Step,
  stepElement: FlowElement | undefined,
  displayName: string,
  type: string = 'any'
) => {
  const stepVariables: Variable[] = []
  stepVariables.push({
    name: wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
    stepName: step.name,
    stepLabel: stepElement?.data.label,
    stepType: step.type,
    displayName: displayName,
    type: type,
    icon: stepElement?.data.icon
  })
  return stepVariables
}

/**
 * Get an array of variables that come from the source object. These contain "properties" which are objects with keys
 *
 * @param {Object} currentObject
 * @param {string} [key]
 * @param {boolean} [isNested]
 * @param {string} [iconUrl] - Default values to apply to every result object
 */
export const getVariablesFromSourceObject = (
  currentObject: SourcePropertyObject,
  key: string,
  iconUrl?: string,
  isNested = false
) => {
  let result: Array<Variable> = []

  result.push({
    displayName: startCase(key),
    icon: iconUrl,
    isNested,
    stepLabel: key,
    stepName: key,
    type: currentObject.type,
    name: key
  })

  if (currentObject.properties) {
    const innerChildren = Object.keys(currentObject.properties).reduce((acc, propertyKey) => {
      const res = getVariablesFromSourceObject(
        currentObject.properties[propertyKey],
        `${key}.${propertyKey}`,
        iconUrl,
        true
      )
      // @ts-expect-error
      acc = acc.concat(res)
      return acc
    }, [])
    result = result.concat(innerChildren)
  }

  return result
}

const getDisplayName = (propertyPath: string, property: string) => {
  // This logic will determine the displayName of a variable. With nested data the context of the parent attribute is important to give context
  const lastPropertyPath = propertyPath.split('].').pop() as string
  const maxVariableNestedDepthDisplay = propertyPath.split('].').length > 1 ? 1 : 4
  let displayName = ''
  if (lastPropertyPath?.split('.').length > maxVariableNestedDepthDisplay) {
    displayName = lastPropertyPath
      .split('.')
      .slice(-2)
      .map(name => startCase(name))
      .join(' - ')
  } else {
    displayName = startCase(property)
  }
  return displayName
}

export const getVariablesFromJsonSchema = (
  variableStepData: VariableBuilderStepData,
  properties: VariableProperties,
  parentVariableName: string | undefined,
  isArray: boolean = false
) => {
  const stepVariables: Variable[] = []

  for (const property in properties) {
    let propertySuffix = property
    if (isLodashArray(properties)) {
      const inputVariableNameArray = properties[property].name.split('.')
      propertySuffix = inputVariableNameArray.at(-1) as string
    }
    const propertyName = isArray
      ? `${parentVariableName}[0].${propertySuffix}`
      : `${parentVariableName}.${propertySuffix}`
    const isPropertyArray = properties[property].type === FieldTypes.ARRAY
    let nestedProperties
    if (properties[property].properties || properties[property].items?.properties) {
      nestedProperties = getVariablesFromJsonSchema(
        variableStepData,
        properties[property].properties || properties[property].items?.properties,
        propertyName,
        isPropertyArray
      )
    }

    stepVariables.push({
      name: propertyName,
      stepLabel: variableStepData.stepLabel,
      stepName: variableStepData.stepName,
      stepType: variableStepData.stepType,
      isNested: true,
      example:
        properties[property]?.example ||
        properties[property]?.['x-samplevalue'] ||
        (properties[property]?.enum && `[${properties[property]?.enum?.join(', ')}]`),
      enum: properties[property]?.enum || (properties[property].type === 'boolean' ? ['true', 'false'] : undefined),
      displayName: properties[property]['x-displayName'] || getDisplayName(propertyName, propertySuffix),
      type: properties[property].type,
      icon: variableStepData.iconUrl || variableStepData.icon,
      properties: nestedProperties
    })
  }

  return stepVariables
}

const buildTriggerVariableInputProperties = (
  properties: any,
  inputVariable: string,
  step: Step,
  stepElement: FlowElement | undefined
) => {
  let propertiesToReturn: Variable[] = []
  const variableStepData = {
    stepName: step.name,
    stepType: step.type,
    stepLabel: stepElement?.data.label,
    icon: stepElement?.data.icon,
    iconUrl: stepElement?.data.iconUrl
  }
  for (const property of properties) {
    // If property name matches input variable, build trigger property variables to return
    // Else, if property name is included in input variable (input variable is parent), recursively drill down into the property until property name matches input variable
    if (inputVariable === property.name) {
      const output = {
        name: wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
        stepName: step.name,
        stepLabel: stepElement?.data.label,
        stepType: step.type,
        displayName: 'Loop Item',
        type: property.items?.type || (property.properties && typeof property.properties[0]),
        icon: stepElement?.data.icon,
        iconUrl: stepElement?.data.iconUrl
      }

      // If the property is an object with other properties, return those properties along with the initial output
      // Else, just return the initial output
      if (
        (output.type === FieldTypes.OBJECT && !isEmpty(property.items?.properties)) ||
        !isEmpty(property.properties)
      ) {
        propertiesToReturn = getVariablesFromJsonSchema(
          variableStepData,
          property.items?.properties || property.properties,
          wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
          false
        )
      }

      propertiesToReturn.unshift(output)
      return propertiesToReturn
    } else if (inputVariable.includes(property.name) && !isEmpty(property.properties)) {
      const formattedProperties = getVariablesFromJsonSchema(
        variableStepData,
        property.properties,
        wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
        false
      )
      const resolvedInputVariable = inputVariable.replace(property.name, `$.step-${step.stepCount}.output`)
      propertiesToReturn = buildTriggerVariableInputProperties(
        formattedProperties,
        resolvedInputVariable,
        step,
        stepElement
      )
      return propertiesToReturn
    } else if (inputVariable.includes(property.name) && !isEmpty(property.items?.properties)) {
      const formattedProperties = getVariablesFromJsonSchema(
        variableStepData,
        property?.items?.properties,
        wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
        false
      )

      const resolvedInputVariable = inputVariable.replace(property.name, `$.step-${step.stepCount}.output`)
      propertiesToReturn = buildTriggerVariableInputProperties(
        formattedProperties,
        resolvedInputVariable,
        step,
        stepElement
      )
      return propertiesToReturn
    }
  }
  return propertiesToReturn
}

export const buildLoopStepVariables = (
  step: Step,
  stepElement: FlowElement | undefined,
  dataSourcesMapping: Record<string, any>,
  inputStep?: Step | TriggerInput | undefined,
  triggerVariables?: Variable[] | undefined
) => {
  let stepVariables: Variable[] = []
  if (step.stepData.inputVariable) {
    // If input variable is from datasource operation step
    if (inputStep && inputStep?.type !== TriggerTypes.dataSourceEvent) {
      const modelData = inputStep.output || getStepModelData(inputStep, dataSourcesMapping)
      if (modelData) {
        const objectName = modelData['x-vendor-objectname']
        let outputVariableType = FieldTypes.OBJECT

        const variableStepData = {
          stepName: step.name,
          stepLabel: stepElement?.data.label,
          stepType: step.type,
          icon: stepElement?.data.icon,
          iconUrl: stepElement?.data.iconUrl
        }

        if (modelData?.properties) {
          const stepVariableProperties = {}
          let shouldUseModelDataProperties = true
          const parseInputVariable = step.stepData.inputVariable.trim().split('.')
          // If the input variable is a nested property and has properties of it's own, set those properties as variables
          // Else if the datasource operation has filtered fields, only include those fields in the available variables
          if (parseInputVariable.length > 3) {
            const inputVariablePath = join(drop(parseInputVariable, 3), '.properties.')
            const variableProperty = get(modelData.properties, inputVariablePath)
            const nestedProperties = variableProperty?.properties || variableProperty?.items?.properties
            if (!isEmpty(nestedProperties)) {
              for (const key in nestedProperties) {
                set(stepVariableProperties, key, nestedProperties[key])
              }
            }
            outputVariableType = variableProperty?.items?.type || FieldTypes.OBJECT
            shouldUseModelDataProperties = false
          } else if (!isEmpty(inputStep.stepData?.input?.query?.fields)) {
            for (const key in modelData.properties) {
              for (const fieldFilter of inputStep.stepData?.input?.query?.fields ?? []) {
                if (key === fieldFilter) {
                  set(stepVariableProperties, key, modelData.properties[key])
                  break
                }
              }
            }
            shouldUseModelDataProperties = false
          }

          stepVariables = getVariablesFromJsonSchema(
            variableStepData,
            shouldUseModelDataProperties ? modelData.properties : stepVariableProperties,
            wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
            false
          )
        }
        // Set key variable
        const key = {
          name: wrapWithJsonPathVariable(`step-${step.stepCount}.key`),
          stepName: step.name,
          stepLabel: stepElement?.data.label,
          displayName: startCase(objectName || step.stepData.path) || 'Item Key',
          type: FieldTypes.STRING,
          icon: stepElement?.data.icon,
          iconUrl: stepElement?.data.iconUrl
        }
        stepVariables.unshift(key)

        // Set output variable
        const initialStep = {
          name: wrapWithJsonPathVariable(`step-${step.stepCount}.output`),
          stepName: step.name,
          stepLabel: stepElement?.data.label,
          displayName: startCase(objectName || step.stepData.path) || 'Loop Item',
          type: outputVariableType,
          icon: stepElement?.data.icon,
          iconUrl: stepElement?.data.iconUrl
        }
        stepVariables.unshift(initialStep)
      }
    }

    // If input variable is from trigger event
    if (inputStep?.type === TriggerTypes.dataSourceEvent && !!triggerVariables) {
      for (const triggerVariable of triggerVariables) {
        if (inputStep.inputVariablePath.includes(triggerVariable.name) && !isEmpty(triggerVariable.properties)) {
          const triggerInputVariables = buildTriggerVariableInputProperties(
            triggerVariable.properties,
            inputStep.inputVariablePath,
            step,
            stepElement
          )

          if (!isEmpty(triggerInputVariables)) {
            stepVariables.push(...triggerInputVariables)
          }

          break
        }
      }
    }
  }
  return stepVariables
}

export const buildFeatureTriggerVariables = (trigger: HttpTrigger) => {
  return buildVariablesFromTriggerPayload(trigger, <FeatureTriggerIcon />)
}

export const buildHttpTriggerVariables = (trigger: HttpTrigger) => {
  return buildVariablesFromTriggerPayload(trigger, <StyledArrowsOpposite />)
}

const buildVariablesFromTriggerPayload = (
  { name, triggerData, type }: HttpTrigger | MicroappViewTrigger | FeatureTrigger | ManualTrigger,
  icon: any
) => {
  const payload = triggerData?.payload

  if (payload == null) return []

  const schema = convertJSONtoSchema(payload)

  const variableTriggerData = {
    stepName: name || 'Trigger',
    stepType: type,
    stepLabel: 'Trigger',
    icon
  }
  const vars: Variable[] = [
    {
      name: '$.trigger.event',
      stepLabel: 'Trigger',
      stepName: name || 'Trigger',
      stepType: type,
      isNested: false,
      displayName: 'Trigger Event',
      type: 'object',
      icon
    }
  ]
  const properties = getVariablesFromJsonSchema(
    variableTriggerData,
    schema.properties as VariableProperties,
    wrapWithJsonPathVariable('trigger.event')
  )
  vars.push(...properties)
  return vars
}

const buildUserVariables = (trigger: any, user: any, icon: any) => {
  const userVariableData: Variable[] = []

  const userObject = pick(user, ['email', 'displayName', 'title', 'preferredLanguage', 'timezone', 'externalId'])
  const name = get(user, 'name')
  const enterpriseUserAttributes = get(user, 'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User')
  const customAttributes = get(user, `urn:ietf:params:scim:schemas:extension:workgrid:2.0:${user?.organizationId}`)
  const userVariables = { ...userObject, ...name, ...customAttributes, ...enterpriseUserAttributes }

  Object.keys(userVariables).forEach(key => {
    userVariableData.push({
      name: wrapWithJsonPathVariable(`trigger.event.user.${key}`),
      stepName: 'User',
      stepLabel: 'Trigger',
      displayName: startCase(key),
      type: FieldTypes.STRING,
      icon: icon,
      example: userVariables[key]
    })
  })

  const variableData: Variable[] = [
    {
      name: wrapWithJsonPathVariable(`trigger.event.userEmail`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'User Email',
      type: FieldTypes.STRING,
      example: user?.email,
      icon: icon
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.userId`),
      stepName: 'Event',
      stepLabel: 'Trigger',
      displayName: 'User ID',
      type: FieldTypes.STRING,
      example: user?.id,
      icon: icon
    },
    {
      name: wrapWithJsonPathVariable(`trigger.event.user`),
      stepName: 'User',
      stepLabel: 'Trigger',
      displayName: 'User',
      properties: userVariableData,
      type: FieldTypes.OBJECT,
      icon: icon
    }
  ]

  return variableData
}
